import classNames from 'classnames';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import useOnClickOutside from 'react-cool-onclickoutside';
import { Label } from '../Label/Label';

import styles from './Select.module.scss';

type Option<T> = {
  value: T;
  label: string | number;
  subtitle?: string;
  tooltip?: string;
  title?: string;
};

type Props<T> = {
  className?: string;
  disabled?: boolean;
  hasBorder?: boolean;
  inverted?: boolean;
  label?: React.ReactNode;
  name?: string;
  onChange: (value: any) => void;
  options: Option<T>[];
  value: T;
  customMenu?: boolean;
  /** When provided, renders a split-panel dropdown with a rich preview on hover. */
  richPreview?: (value: string) => React.ReactNode;
};

export function Select<T extends string | number = string>(props: Props<T>) {
  const {
    className,
    disabled = false,
    hasBorder = true,
    inverted = false,
    label = '',
    name,
    onChange,
    options,
    value,
    customMenu = true,
    richPreview,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; width: number; openAbove: boolean }>({
    top: 0,
    left: 0,
    width: 0,
    openAbove: true,
  });
  const [hoveredValue, setHoveredValue] = useState<string | null>(null);

  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const clickOutsideRef = useOnClickOutside((event) => {
    if (menuRef.current && menuRef.current.contains(event.target as Node)) {
      return;
    }
    setIsOpen(false);
  });

  const updateCoords = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      const menuHeight = richPreview ? 360 : (menuRef.current ? menuRef.current.offsetHeight : 280);

      // When richPreview is set, prefer opening above; fall back to below if insufficient space
      const preferAbove = richPreview ? true : false;
      let openAbove: boolean;

      if (preferAbove) {
        openAbove = spaceAbove >= menuHeight || spaceAbove > spaceBelow;
      } else {
        openAbove = spaceBelow < menuHeight && spaceAbove > spaceBelow;
      }

      if (openAbove) {
        setCoords({
          top: rect.top + window.scrollY - menuHeight - 4,
          left: rect.left + window.scrollX,
          width: rect.width,
          openAbove: true,
        });
      } else {
        setCoords({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: rect.width,
          openAbove: false,
        });
      }
    }
  }, [richPreview]);

  useEffect(() => {
    if (isOpen) {
      // Set initial hovered value to current selection, or first non-empty option if nothing selected
      if (value != null && String(value) !== '') {
        setHoveredValue(String(value));
      } else {
        const firstNonEmpty = options?.find((o) => o.value !== '' && o.value != null);
        setHoveredValue(firstNonEmpty ? String(firstNonEmpty.value) : null);
      }
      updateCoords();
      const timer = setTimeout(updateCoords, 0);
      window.addEventListener('resize', updateCoords);
      window.addEventListener('scroll', updateCoords, true);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', updateCoords);
        window.removeEventListener('scroll', updateCoords, true);
      };
    }
  }, [isOpen, updateCoords]);

  const handleSelectOption = (optValue: any, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onChange(optValue);
    setIsOpen(false);
  };

  const selectedOption = options?.find((o) => o.value === value);

  if (!customMenu) {
    return (
      <div className={classNames(styles.root, { [styles.inverted]: inverted, [styles.hasBorder]: hasBorder }, className)}>
        <Label inverted={inverted} text={label}>
          <select name={name} disabled={disabled} onChange={(e) => onChange(e.currentTarget.value)} value={value}>
            {options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Label>
      </div>
    );
  }

  const previewValue = hoveredValue ?? (value != null ? String(value) : '');

  const renderDropdown = () => {
    if (!richPreview) {
      // Standard dropdown without preview panel
      return (
        <div
          ref={menuRef}
          className={styles.portalledDropdownMenu}
          style={{
            position: 'absolute',
            top: `${coords.top}px`,
            left: `${coords.left}px`,
            minWidth: `${coords.width}px`,
            width: 'max-content',
            zIndex: 99999,
          }}
        >
          {options?.map((option) => (
            <div
              key={option.value}
              className={classNames(styles.dropdownOption, { [styles.isSelected]: option.value === value })}
              onMouseDown={(e) => handleSelectOption(option.value, e)}
              onClick={(e) => handleSelectOption(option.value, e)}
            >
              <span className={styles.optionLabel}>{option.label}</span>
            </div>
          ))}
        </div>
      );
    }

    // Rich preview split-panel dropdown
    // Right-edge of options list aligns with trigger field right edge
    const optionsRight = coords.left + coords.width;

    return (
      <div
        ref={menuRef}
        className={classNames(styles.richDropdownWrapper, {
          [styles.richDropdownAbove]: coords.openAbove,
        })}
        style={{
          position: 'absolute',
          top: `${coords.top}px`,
          right: undefined,
          zIndex: 99999,
        }}
      >
        {/* 
          Position so options list right edge = trigger right edge.
          We use left positioning based on trigger. The wrapper will have both panels.
          Clamped to viewport bounds to prevent overflow at narrower widths.
        */}
        <div
          className={styles.richDropdownInner}
          style={{
            left: (() => {
              const previewWidth = 260;
              const optionsWidth = Math.max(coords.width, 280);
              const totalWidth = previewWidth + optionsWidth;
              const viewportWidth = document.documentElement.clientWidth;
              const scrollX = window.scrollX;
              const margin = 16;

              // Ideal: right-align options list with trigger right edge
              let left = coords.left + coords.width - totalWidth;

              // Clamp: don't overflow right edge
              if (left + totalWidth > scrollX + viewportWidth - margin) {
                left = scrollX + viewportWidth - margin - totalWidth;
              }
              // Clamp: don't overflow left edge
              if (left < scrollX + margin) {
                left = scrollX + margin;
              }

              return `${left}px`;
            })(),
          }}
        >
          <div className={styles.richPreviewPanel}>
            {richPreview(previewValue)}
          </div>
          <div
            className={styles.richOptionsList}
            style={{ minWidth: `${Math.max(coords.width, 280)}px` }}
          >
            {options?.filter((option) => option.value !== '' && option.value != null).map((option) => (
              <div
                key={option.value}
                className={classNames(styles.richOption, {
                  [styles.richOptionSelected]: option.value === value,
                  [styles.richOptionHovered]: String(option.value) === hoveredValue,
                })}
                onMouseEnter={() => setHoveredValue(String(option.value))}
                onMouseDown={(e) => handleSelectOption(option.value, e)}
                onClick={(e) => handleSelectOption(option.value, e)}
              >
                <span className={styles.richOptionLabel}>{option.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      ref={clickOutsideRef as any}
      className={classNames(
        styles.root,
        styles.customSelectRoot,
        { [styles.inverted]: inverted, [styles.hasBorder]: hasBorder, [styles.isOpen]: isOpen },
        className
      )}
    >
      <Label inverted={inverted} text={label}>
        {name && <input type="hidden" name={name} value={value ?? ''} />}
        <div
          ref={triggerRef}
          className={classNames(styles.trigger, { [styles.disabled]: disabled })}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              !disabled && setIsOpen(!isOpen);
            } else if (e.key === 'Escape') {
              setIsOpen(false);
            }
          }}
        >
          <span className={styles.selectedValue}>{selectedOption ? selectedOption.label : value || ''}</span>
        </div>
        {isOpen && ReactDOM.createPortal(renderDropdown(), document.body)}
      </Label>
    </div>
  );
}
